import { useContext, useEffect, useRef, useState } from 'react';

import { User } from 'firebase/auth';

import reply from '../../../assets/images/icons/reply.svg';
import thumbsDown from '../../../assets/images/icons/thumb_down.svg';
import thumbsUp from '../../../assets/images/icons/thumb_up.svg';
import AnonymousRoundedImage from '../../../components/atoms/rounded-image/AnonymousRoundedImage';
import CommentData from '../../../types/commentData';
import { AuthContext } from '../../../utils/AuthProvider';
import { useUserProfile } from '../../../utils/hooks';
import UserRoundedImage from '../../atoms/rounded-image/UserRoundedImage';
import { Icon } from '@iconify/react';

import './Comment.scss';

interface CommentCardProps {
  comment: CommentData;
  repliedToComment?: CommentData;
  onThumbsUp?: (comment: CommentData, user: User | null) => void;
  onThumbsDown?: (comment: CommentData, userId: User | null) => void;
  onClickReply?: () => void;
  onDeleteComment: (comment: CommentData) => void;
  className?: string;
}

export default function Comment({
  comment,
  onThumbsUp,
  onThumbsDown,
  repliedToComment,
  onClickReply,
  onDeleteComment,
  className = '',
}: CommentCardProps) {
  const [isOptionsOpen, setIsOptionsOpen] = useState<boolean>(false);
  const optionsMenuRef = useRef<HTMLUListElement>(null);
  const openMenuButtonRef = useRef<SVGSVGElement>(null);

  const { user } = useContext(AuthContext)!;
  const userProfile = useUserProfile(comment.userId);

  function onClickReplyInOptions() {
    if (onClickReply) {
      onClickReply();
    }
    setIsOptionsOpen(false);
  }
  function onClickDelete(comment: CommentData) {
    onDeleteComment(comment);
    setIsOptionsOpen(false);
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        optionsMenuRef.current &&
        openMenuButtonRef.current &&
        !optionsMenuRef.current.contains(event.target as Node) &&
        !openMenuButtonRef.current.contains(event.target as Node)
      ) {
        setIsOptionsOpen(false);
      }
    }

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [optionsMenuRef]);

  return (
    <div className={`comment ${className}`}>
      {userProfile &&
        (comment.userId !== null ? (
          <UserRoundedImage className="shrink-0" userProfile={userProfile} />
        ) : (
          <AnonymousRoundedImage className="shrink-0" />
        ))}
      <div>
        <div className="comment-row">
          <div className="comment-card-container">
            <div className="comment-card">
              {repliedToComment && (
                <div className="reply-row mt-1">
                  <img className="reply-icon" src={reply} alt="Reply" />
                  <span className="reply-to-comment">{repliedToComment.body}</span>
                </div>
              )}
              {userProfile !== undefined && (
                <div className="author">
                  {userProfile ? userProfile.firstName + ' ' + userProfile.lastName : 'Anonymous'}
                </div>
              )}
              <p className={comment.isDeleted ? 'comment-deleted' : 'comment-body'}>{comment.body}</p>
            </div>
          </div>
        </div>
        <div className="actions-row">
          {onClickReply &&
            !comment.isDeleted &&
            (user?.uid === comment.userId ? (
              <div className="mr-auto options-container">
                <button onClick={() => setIsOptionsOpen(!isOptionsOpen)}>
                  <Icon ref={openMenuButtonRef} icon="mdi:dots-horizontal" width="1.5rem" height="2.5rem" />
                </button>
                {isOptionsOpen && (
                  <ul ref={optionsMenuRef} className="options-list">
                    <li onClick={onClickReplyInOptions}>Reply</li>
                    <li>Edit</li>
                    <li onClick={() => onClickDelete(comment)}>Delete</li>
                  </ul>
                )}
              </div>
            ) : (
              <button className="mr-auto reply-button" onClick={onClickReply}>
                Reply
              </button>
            ))}
          <button className="flex flex-row items-center" onClick={() => onThumbsUp?.(comment, user)}>
            <span className="mr-0.5">{comment.upUuids.length}</span>
            <img className="thumb-icon mr-1.5" src={thumbsUp} alt="Thumbs up" />
          </button>
          <button className="flex flex-row items-center" onClick={() => onThumbsDown?.(comment, user)}>
            <span className="mr-0.5">{comment.downUuids.length}</span>
            <img className="thumb-icon" src={thumbsDown} alt="Thumbs down" />
          </button>
        </div>
      </div>
    </div>
  );
}
